import { FC, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { IconButton, DialogTitle, DialogContent, Stack, Chip, Box, Typography } from '@mui/material';

import Button from '@mui/lab/LoadingButton';
import CloseIcon from '@mui/icons-material/Close';
import { getSchedulerRoomsAbility, importSchedulerAbility } from 'api/schedulersettings';

type Props = {
    /**
     * Функция закрытия диалогового окна.
     */
    close: () => void;

    /**
     * Активный рум.
     */
    active: string[];
}

/**
 * Диалоговое окно для импорта данных сетки ABI в расписторе.
 */
const SchedulerAbilityImportDialog: FC<Props> = ({ active, ...props }) => {
    const queryClient = useQueryClient();
    const [selectedRooms, setSelectedRooms] = useState(active);
    const [fromTable, setFromTable] = useState<string | undefined>();
    const [toTable, setToTable] = useState<string | undefined>();

    const { handleSubmit } = useForm();

    const { data: abilityRooms } = useQuery(['scheduler-ability-rooms'], getSchedulerRoomsAbility, {
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
    },)

    const importItemMutation = useMutation(importSchedulerAbility, {
        onSuccess: () => {
            queryClient.invalidateQueries('scheduler-ability-data-regular');
            queryClient.invalidateQueries('scheduler-ability-data-turbo');
            queryClient.invalidateQueries('scheduler-ability-data-rebuy');

            props.close();
        },
    });

    /**
     * Функция сохранения формы.
     */
    const onSubmit = handleSubmit(data => {
        const importData = {
            to_network : active[0],
            from_network : selectedRooms[0],
            from_type: fromTable,
            to_type: toTable,
        }

        importItemMutation.mutate({ body: importData })
    });

    /**
     * Функция выбора таблицы импорта.
     */
    function handleChangeFrom(type:string) {
        setFromTable(type)
        setToTable(undefined);
    }

    return (
        <>
            <DialogTitle>
                Импорт данных
                <IconButton
                    aria-label='close'
                    onClick={() => {
                        props.close();
                    }}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <form className='flex flex-col gap-4' onSubmit={onSubmit}>
                    <Box>
                        <Typography className='mb-1'>Импортировать из</Typography>
                        <Stack direction="row" spacing={1}>
                            {abilityRooms.networks.map((item, index) => {
                                return <Chip key={index} label={item} color={item === selectedRooms[0] ? 'primary' : 'default'} onClick={() => setSelectedRooms([item])} clickable />
                            })}
                        </Stack>
                    </Box>
                    <Box>
                        <Typography className='mb-1'>Копировать из таблицы:</Typography>
                        <Stack direction="row" spacing={1}>
                            <Chip label='Regular' color={fromTable === 'regular' ? 'primary' : 'default'} onClick={() => handleChangeFrom('regular')} clickable />
                            <Chip label='Turbo' color={fromTable === 'turbo' ? 'primary' : 'default'} onClick={() => handleChangeFrom('turbo')} clickable />
                            <Chip label='Rebuy' color={fromTable === 'rebuy' ? 'primary' : 'default'} onClick={() => handleChangeFrom('rebuy')} clickable />
                        </Stack>
                    </Box>
                    <Box>
                        <Typography className='mb-1'>Копировать в таблицу:</Typography>
                        <Stack direction="row" spacing={1} >
                            <Chip label='Regular' color={toTable === 'regular' ? 'primary' : 'default'} onClick={() => setToTable('regular')} clickable disabled={!fromTable} />
                            <Chip label='Turbo' color={toTable === 'turbo' ? 'primary' : 'default'} onClick={() => setToTable('turbo')} clickable disabled={!fromTable} />
                            <Chip label='Rebuy' color={toTable === 'rebuy' ? 'primary' : 'default'} onClick={() => setToTable('rebuy')} clickable disabled={!fromTable} />
                        </Stack>
                    </Box>
                    <div className='pt-4'>
                        <Button
                            fullWidth
                            variant='contained'
                            size='large'
                            type='submit'
                            loading={importItemMutation.isLoading}
                            disabled={!fromTable || !toTable ? true : false}
                        >
                            Импортировать
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </>
    );
};

export default SchedulerAbilityImportDialog;
